// JST (+09:00) 固定用ユーティリティ（date-fns-tz 依存不要）
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
const YMD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** YYYY-MM-DD -> UTC ISO (その日の UTC 00:00:00Z に統一) */
export function ymdToUtcIso(ymd: string): string {
  if (!ymd || !YMD_REGEX.test(ymd)) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 0, 0, 0)).toISOString();
}

/** UTC ISO -> YYYY-MM-DD (JST表示) 既存の 15:00Z(前日) も +9h 補正で正しい日へ */
export function isoToYmd(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const jst = new Date(d.getTime() + JST_OFFSET_MS);
  const y = jst.getUTCFullYear();
  const m = String(jst.getUTCMonth() + 1).padStart(2, "0");
  const day = String(jst.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** 今日 (JST) */
export function todayYmd(): string {
  return isoToYmd(new Date().toISOString());
}

/** ISO / YYYY-MM-DD -> YYYY-MM-DD(JST) */
export function normalizeToYmd(v: string): string {
  if (!v) return "";
  if (YMD_REGEX.test(v)) return v;
  return isoToYmd(v);
}

/**
 * 送信用正規化: 常に「その暦日(JST)の UTC 00:00Z」へ統一する。
 * サーバーは SQL Server の datetime を Kind=Unspecified で読み戻すため、
 * 返却値には Z が付かない。Z 無しの ISO を new Date().toISOString() すると
 * ブラウザが JST ローカルと誤解釈し 9 時間ズレる（前日の 15:00Z 化）ため、
 * いったん JST 日付へ正規化(isoToYmd)してから UTC 00:00Z(ymdToUtcIso)に戻す。
 * これにより Z の有無に関わらず冪等になる。
 */
export function normalizeOutgoingDate(v?: string): string | undefined {
  if (!v) return undefined;
  if (YMD_REGEX.test(v)) return ymdToUtcIso(v);
  const ymd = isoToYmd(v);
  return ymd ? ymdToUtcIso(ymd) : undefined;
}

/** JST 日付でオーバーラップ判定 */
export function isDateRangeOverlap(
  taskStartIso: string | undefined,
  taskEndIso: string | undefined,
  filterStartYmd: string | undefined,
  filterEndYmd: string | undefined
): boolean {
  const startY = taskStartIso ? isoToYmd(taskStartIso) : "";
  const endY = taskEndIso ? isoToYmd(taskEndIso) : startY;
  if (filterStartYmd && endY && endY < filterStartYmd) return false;
  if (filterEndYmd && startY && startY > filterEndYmd) return false;
  return true;
}

/** ISO を JST 日単位で days 加算（内部的に YMD 化 → 変換） */
export function addDaysIso(iso: string, days: number): string {
  if (!iso) return iso;
  const base = isoToYmd(iso);
  if (!base) return iso;
  return ymdToUtcIso(addDaysYmd(base, days));
}

/** YYYY-MM-DD に days 加算 */
export function addDaysYmd(ymd: string, days: number): string {
  if (!YMD_REGEX.test(ymd)) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days, 0, 0, 0));
  return isoToYmd(dt.toISOString());
}

/** 同一 JST 日付か */
export function sameDayJst(a?: string, b?: string): boolean {
  if (!a || !b) return false;
  return isoToYmd(a) === isoToYmd(b);
}
